import React, { Suspense, useState } from 'react';
import { RefreshCw, Database, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSupabaseSearch } from '../context/SupabaseSearchContext';
import { MockDataToggle } from './MockDataToggle';
import { ITEMS_PER_PAGE } from '../hooks/useSupabaseFoundationSearch';
import { formatNumber } from '../utils/formatNumber';
import { LoadingCard } from './LoadingCard';
import { ErrorBoundary } from './ErrorBoundary';
import PremiumFeatures from './PremiumFeatures';
import { FreeFeatures } from './FreeFeatures';
import { PaymentModal } from './PaymentModal';
import { ViewModeToggle } from './ViewModeToggle';
import { useViewMode } from '../context/ViewModeContext';
import { Foundation } from '../types/Foundation';

const FoundationCard = React.lazy(() => import('./FoundationCard'));

// Component to handle different grid layouts based on view mode
const ResultsGrid = ({ filteredResults }: { filteredResults: Foundation[] }) => {
  const { viewMode } = useViewMode();
  
  // Helper to get number of columns based on window width
  // Use this inside component to ensure it's updated on window resize
  const getColumnCount = () => {
    // We need to check window width directly since we're doing this client-side
    // Match the breakpoints in Tailwind: md is 768px, lg is 1024px
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) return 3; // lg: screens
      if (window.innerWidth >= 768) return 2;  // md: screens
      return 1; // Default for mobile
    }
    return 3; // Default for server-side rendering
  };

  if (viewMode === 'compact') {
    // Horizontal grid layout for compact view (row-by-row)
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 relative z-10">
        {filteredResults.map((foundation, index) => (
          <div key={foundation.name + '-' + index}>
            <FoundationCard foundation={foundation} index={index} />
          </div>
        ))}
      </div>
    );
  } else {
    // Vertical column layout for expanded view (column-by-column)
    const [columnCount, setColumnCount] = useState(getColumnCount());
    
    // Update column count on window resize
    React.useEffect(() => {
      const handleResize = () => {
        setColumnCount(getColumnCount());
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Create empty arrays for each column
    const columns: Foundation[][] = Array.from({ length: columnCount }, () => []);
    
    // Distribute items vertically across columns
    filteredResults.forEach((foundation, index) => {
      const columnIndex = index % columnCount;
      columns[columnIndex].push(foundation);
    });
    
    // Get proper CSS classes based on column count 
    const getGridClasses = () => {
      let classes = "grid grid-cols-1 gap-4 sm:gap-6 relative z-10";
      
      if (columnCount > 1) {
        classes += " md:grid-cols-2";
      }
      
      if (columnCount > 2) {
        classes += " lg:grid-cols-3";
      }
      
      return classes;
    };
    
    return (
      <div className={getGridClasses()}>
        {columns.map((column, colIndex) => (
          <div key={`column-${colIndex}`} className="flex flex-col gap-4 sm:gap-6">
            {column.map((foundation, foundationIndex) => {
              // For vertical layout, we need to consider position across columns
              // Use absolute position to ensure a unique index for each card
              // This ensures ads are distributed evenly across all columns
              
              // For example, with 3 columns and 9 items, we want:
              // Column 1: Items 0,3,6 (indexes 0,3,6)
              // Column 2: Items 1,4,7 (indexes 1,4,7)
              // Column 3: Items 2,5,8 (indexes 2,5,8)
              
              // Calculate position based on column index and position within column
              const verticalPosition = foundationIndex * columnCount + colIndex;
              
              return (
                <div key={foundation.name + '-' + colIndex + '-' + foundationIndex}>
                  <FoundationCard foundation={foundation} index={verticalPosition} />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }
};

// Pagination component to be used at top and bottom of results
const Pagination = ({ 
  currentPage, 
  totalPages, 
  nextPage, 
  previousPage, 
  goToPage 
}: { 
  currentPage: number; 
  totalPages: number; 
  nextPage: () => void; 
  previousPage: () => void; 
  goToPage: (page: number) => void; 
}) => {
  // Calculate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    // Always show first page
    pages.push(1);
    
    // Calculate range around current page
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);
    
    // Add ellipsis after first page if there's a gap
    if (startPage > 2) {
      pages.push('...');
    }
    
    // Add pages in range
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add ellipsis before last page if there's a gap
    if (endPage < totalPages - 1) {
      pages.push('...');
    }
    
    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center my-4 relative w-full max-w-md mx-auto">
      {/* Static left arrow */}
      <button
        onClick={previousPage}
        disabled={currentPage === 1}
        className={`p-2 rounded-md absolute left-0 ${
          currentPage === 1 
            ? 'text-gray-400 cursor-not-allowed' 
            : 'text-[#A3C1DA] hover:bg-[#A3C1DA]/10'
        }`}
        aria-label="Föregående sida"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      
      {/* Center pagination numbers */}
      <div className="flex space-x-1 mx-auto">
        {getPageNumbers().map((page, index) => (
          page === '...' ? (
            <span key={`ellipsis-${index}`} className="px-3 py-1">...</span>
          ) : (
            <button
              key={`page-${page}`}
              onClick={() => goToPage(page as number)}
              className={`px-3 py-1 rounded-md ${
                currentPage === page 
                  ? 'bg-[#F2C1D1] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {page}
            </button>
          )
        ))}
      </div>
      
      {/* Static right arrow */}
      <button
        onClick={nextPage}
        disabled={currentPage === totalPages}
        className={`p-2 rounded-md absolute right-0 ${
          currentPage === totalPages 
            ? 'text-gray-400 cursor-not-allowed' 
            : 'text-[#A3C1DA] hover:bg-[#A3C1DA]/10'
        }`}
        aria-label="Nästa sida"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

export function SearchResults() {
  const { 
    query,
    searchResults,
    filteredResults, 
    totalFoundations,
    totalResults,
    loading, 
    error,
    currentPage,
    totalPages,
    nextPage,
    previousPage,
    goToPage,
    retryFetch,
    refreshData,
    isMockMode
  } = useSupabaseSearch();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const handlePaymentClick = () => {
    setIsPaymentModalOpen(true);
  };

  return (
    <main className="flex-grow max-w-7xl mx-auto px-4 py-8 w-full relative" role="main">
      {/* Developer tools are now shown via the DeveloperTools component */}
      <ErrorBoundary>
        <Suspense fallback={
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {Array(6).fill(null).map((_, i) => <LoadingCard key={i} />)}
          </div>
        }>
          {error ? (
            <div>
              <div className="text-center py-6 mb-8">
                <p className="text-[#A3C1DA] mb-4" role="alert">{error}</p>
                <button
                  onClick={retryFetch}
                  className="inline-flex items-center px-4 py-2 bg-[#F2C1D1] text-white rounded-md hover:bg-[#D1B3C4] transition-colors focus:outline-none focus:ring-2 focus:ring-[#A3C1DA] focus:ring-offset-2"
                >
                  <RefreshCw className="w-5 h-5 mr-2" aria-hidden="true" />
                  Försök igen
                </button>
              </div>
              
              {/* Show features even when backend is disconnected */}
              <FreeFeatures />
              <PremiumFeatures onPaymentClick={handlePaymentClick} />
            </div>
          ) : loading && searchResults.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {Array(6).fill(null).map((_, i) => <LoadingCard key={i} />)}
            </div>
          ) : query ? (
            <>
              <div className="mb-6 space-y-4">
                {isMockMode && (
                  <div className="text-center bg-yellow-100 border border-yellow-300 py-2 px-4 rounded-lg shadow-sm mb-3">
                    <p className="text-yellow-800 font-medium">
                      Visar 100 mockade stiftelser (utvecklingsläge)
                    </p>
                  </div>
                )}
                <div className="text-center bg-gradient-to-r from-[#F2C1D1]/10 to-[#A3C1DA]/10 py-3 px-4 rounded-lg shadow-sm">
                  <p className="text-gray-700 text-lg font-medium" role="status">
                    {totalResults === 0 
                      ? 'Inga stiftelser hittades för din sökning.'
                      : `Din sökning hittade ${formatNumber(totalResults)} stiftelser av ${formatNumber(totalFoundations)} möjliga (Sida ${currentPage} av ${totalPages}, ${formatNumber(Math.min(ITEMS_PER_PAGE, filteredResults.length))} per sida)`}
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    {totalResults > 0 && `Sökt på "${query}"`}
                  </p>
                  <div className="flex flex-col items-center space-y-2">
                    <div className="flex items-center justify-center text-xs text-gray-500">
                      <Database size={12} className="mr-1" />
                      <span>Databasen uppdateras dagligen</span>
                      <button
                        onClick={refreshData}
                        className="ml-2 flex items-center text-[#A3C1DA] hover:text-[#7A98B0]"
                        title="Uppdatera databasen manuellt"
                      >
                        <RefreshCw size={12} className="mr-1" />
                        <span>Uppdatera nu</span>
                      </button>
                    </div>
                    
                    <MockDataToggle className="mt-2" />
                  </div>
                </div>
                
                {filteredResults.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex justify-center gap-4">
                      <ViewModeToggle />
                      <button
                        onClick={() => {
                          // First save all foundations on the current page
                          filteredResults.forEach(foundation => {
                            const saveEvent = new CustomEvent('saveFoundation', { detail: foundation });
                            window.dispatchEvent(saveEvent);
                          });
                          
                          // Then open the foundations manager
                          window.dispatchEvent(new CustomEvent('toggleFoundationsManager'));
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-[#A3C1DA] text-white shadow-md rounded-md hover:bg-[#7A98B0] 
                                 transition-all text-sm font-medium"
                        aria-label="Spara och se alla stiftelser"
                      >
                        <span>Spara och öppna stiftelsehanteraren</span>
                      </button>
                    </div>
                    
                    {/* Top pagination */}
                    <Pagination 
                      currentPage={currentPage}
                      totalPages={totalPages}
                      nextPage={nextPage}
                      previousPage={previousPage}
                      goToPage={goToPage}
                    />
                  </div>
                )}
              </div>

              <ResultsGrid filteredResults={filteredResults} />
              
              {loading ? (
                <div className="text-center py-8" role="status">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A3C1DA] mx-auto"></div>
                  <p className="mt-4 text-gray-600">Laddar resultat...</p>
                </div>
              ) : filteredResults.length > 0 && (
                /* Bottom pagination */
                <Pagination 
                  currentPage={currentPage}
                  totalPages={totalPages}
                  nextPage={nextPage}
                  previousPage={previousPage}
                  goToPage={goToPage}
                />
              )}
            </>
          ) : (
            <>
              <FreeFeatures />
              <PremiumFeatures onPaymentClick={handlePaymentClick} />
            </>
          )}
        </Suspense>
      </ErrorBoundary>

      <PaymentModal 
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />
    </main>
  );
}
